import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AnswerControls, isAnswered } from '@/components/journey/AnswerControls';
import { CreatorReply } from '@/components/journey/CreatorReply';
import { ProgressTrack } from '@/components/journey/ProgressTrack';
import { Button } from '@/components/ui/Button';
import { useCelebration } from '@/components/ui/Celebration';
import { getCreatorContent } from '@/content/registry';
import { celebrationFor } from '@/domain/celebration';
import { roundContaining } from '@/domain/rounds';
import { alignmentFor } from '@/domain/scoring';
import type { JourneyState, VisitorAnswer } from '@/domain/types';
import { track } from '@/lib/analytics';
import { usePrefersReducedMotion, useDocumentTitle } from '@/lib/hooks';
import { canPersist, createJourneyState, loadJourney, saveJourney } from '@/lib/storage';
import journeyStyles from '@/components/journey/journey.module.css';
import styles from './JourneyPage.module.css';

/** Identifies one specific answer, so a celebration fires once and not again. */
function answerKey(answer: VisitorAnswer): string {
  switch (answer.kind) {
    case 'single':
      return `${answer.questionId}:${answer.optionId}`;
    case 'multi':
      return `${answer.questionId}:${[...answer.optionIds].sort().join('+')}`;
    case 'scale':
      return `${answer.questionId}:${answer.value}`;
  }
}

export function JourneyPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const content = getCreatorContent(slug);

  /** Arrived by choosing more questions on the result page, rather than returning. */
  const continuing = Boolean((location.state as { continued?: boolean } | null)?.continued);
  const reducedMotion = usePrefersReducedMotion();
  const { celebrate, layer } = useCelebration();

  const promptRef = useRef<HTMLHeadingElement | null>(null);
  const replyRef = useRef<HTMLElement | null>(null);
  const startedRef = useRef(false);
  /** Answers already celebrated this visit. Going back does not re-throw sparks. */
  const celebratedRef = useRef<Set<string>>(new Set());

  const [state, setState] = useState<JourneyState | null>(() => {
    if (!content) return null;
    return loadJourney(content.profile.id) ?? createJourneyState(content.profile.id);
  });

  const [resumed, setResumed] = useState(false);
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);

  useDocumentTitle(
    content ? `Questions - ${content.profile.displayName}` : 'Are We Compatible?',
  );

  useEffect(() => {
    if (!content || !state || startedRef.current) return;
    startedRef.current = true;

    /*
      Carrying on from the result page is not a return visit. `journey_continued`
      was already recorded there, and greeting her with "picked up right where
      you left off" one screen after she pressed a button is a small lie the
      page should not tell.
    */
    if (continuing) return;

    const answeredCount = Object.keys(state.answers).length;
    if (answeredCount > 0) {
      setResumed(true);
      setShowResumeBanner(true);
      track({ name: 'journey_resumed', profileId: content.profile.id });
    } else {
      track({ name: 'journey_started', profileId: content.profile.id });
    }
  }, [content, state, continuing]);

  useEffect(() => {
    if (state) saveJourney(state);
  }, [state]);

  const index = state?.currentIndex ?? 0;
  const questions = content?.questions ?? [];
  const rounds = content?.rounds ?? [];
  const question = questions[index];
  const answer = question ? state?.answers[question.id] : undefined;
  const answered = isAnswered(answer);

  /**
   * The batch this question belongs to. Everything the page says about
   * progress is relative to it — "4 of 10", not "4 of 17" — because the round
   * is the commitment she actually made, and a bar creeping towards seventeen
   * would misrepresent it.
   */
  const round = roundContaining(rounds, index);
  const isLastInRound = round ? index === round.endIndex - 1 : index === questions.length - 1;
  const isLastOfAll = index === questions.length - 1;

  const editing = Boolean(question) && (!answered || editingQuestionId === question.id);
  const comparingMulti = question?.kind === 'multi' && editing;

  /** How the two answers landed. Only meaningful once the comparison is shown. */
  const alignment = useMemo(() => {
    if (!question || !answer || editing) return null;
    return alignmentFor(question, answer);
  }, [question, answer, editing]);

  useEffect(() => {
    if (!question) return;
    promptRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
    if (content) {
      track({
        name: 'question_reached',
        profileId: content.profile.id,
        questionIndex: index,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id]);

  /**
   * The one place anything celebrates.
   *
   * Only a genuine match earns particles, only once per answer, and only when
   * the comparison is actually on screen. Everything softer — a near miss, a
   * difference — is handled by `CreatorReply` in CSS, so reduced motion loses
   * the movement without losing the meaning.
   */
  useEffect(() => {
    if (!answer || !question || editing) return;
    if (celebrationFor(alignment, question) !== 'sparkle') return;

    const key = answerKey(answer);
    if (celebratedRef.current.has(key)) return;
    celebratedRef.current.add(key);

    celebrate(replyRef.current, 'sparkle');
  }, [alignment, answer, editing, celebrate, question]);

  const handleAnswer = useCallback(
    (next: VisitorAnswer) => {
      setState((current) =>
        current
          ? {
              ...current,
              answers: { ...current.answers, [next.questionId]: next },
              updatedAt: new Date().toISOString(),
            }
          : current,
      );

      // Single answers can reveal immediately. Multi-select answers stay open
      // until she says she is finished choosing.
      setEditingQuestionId(question?.kind === 'multi' ? next.questionId : null);
      setShowResumeBanner(false);
    },
    [question?.kind],
  );

  const goTo = useCallback((nextIndex: number) => {
    setState((current) =>
      current ? { ...current, currentIndex: Math.max(0, nextIndex) } : current,
    );
    setEditingQuestionId(null);
    setShowResumeBanner(false);
  }, []);

  /**
   * The end of a round is a real stop, not a checkpoint. She goes to the
   * result, sees where the two of you landed on what she has actually
   * answered, and decides from there whether to get in touch, carry on, or
   * leave it — all three of which are fine endings.
   */
  const handleNext = useCallback(() => {
    if (!content) return;
    if (isLastInRound) {
      if (round) {
        track({
          name: 'journey_round_completed',
          profileId: content.profile.id,
          roundIndex: round.index,
        });
      }
      if (isLastOfAll) {
        track({ name: 'journey_completed', profileId: content.profile.id });
      }
      setState((current) => (current ? { ...current, currentIndex: index } : current));
      navigate(`/${content.profile.slug}/result`);
      return;
    }
    goTo(index + 1);
  }, [content, goTo, index, isLastInRound, isLastOfAll, navigate, round]);

  const handlePrimaryAction = useCallback(() => {
    if (comparingMulti && answered) {
      setEditingQuestionId(null);
      return;
    }
    handleNext();
  }, [answered, comparingMulti, handleNext]);

  if (!content) return <Navigate to="/404" replace />;
  if (!state || !question) return <Navigate to={`/${slug}`} replace />;

  const { profile } = content;
  const primaryLabel = comparingMulti
    ? 'See how we answered'
    : isLastInRound
      ? 'See where we landed'
      : 'Keep going';

  return (
    <div className={styles.page}>
      <ProgressTrack
        current={round ? index - round.startIndex : index}
        total={round ? round.questions.length : questions.length}
        theme={question.theme}
        questionId={question.id}
        roundLabel={round?.meta.label}
      />

      <div className={styles.body}>
        {showResumeBanner && resumed ? (
          <p className={styles.resumeBanner}>
            <span aria-hidden="true">👋</span>
            <span>Picked up right where you left off.</span>
            <button
              type="button"
              className={styles.resumeDismiss}
              onClick={() => setShowResumeBanner(false)}
            >
              Got it
            </button>
          </p>
        ) : null}

        <section key={question.id} className={journeyStyles.question}>
          {question.scenario ? (
            <p className={journeyStyles.scenario}>{question.scenario}</p>
          ) : null}

          <h1 className={journeyStyles.prompt} tabIndex={-1} ref={promptRef}>
            {question.prompt}
          </h1>

          {question.help ? <p className={journeyStyles.help}>{question.help}</p> : null}

          {editing ? (
            <AnswerControls question={question} answer={answer} onChange={handleAnswer} />
          ) : answer ? (
            <>
              <CreatorReply
                ref={replyRef}
                question={question}
                profile={profile}
                visitorAnswer={answer}
                alignment={alignment}
              />
              <p className={journeyStyles.editAnswer}>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => setEditingQuestionId(question.id)}
                >
                  Change my answer
                </Button>
              </p>
            </>
          ) : null}
        </section>
      </div>

      <div className={journeyStyles.actions}>
        <div className={journeyStyles.actionsRow}>
          {index > 0 ? (
            <Button
              variant="secondary"
              className={journeyStyles.back}
              onClick={() => goTo(index - 1)}
            >
              Back
            </Button>
          ) : null}

          <Button
            onClick={handlePrimaryAction}
            disabled={!answered}
            withArrow={answered}
            aria-describedby={answered ? undefined : 'next-hint'}
          >
            {primaryLabel}
          </Button>
        </div>

        {!answered ? (
          <p id="next-hint" className={journeyStyles.exit}>
            {question.kind === 'multi' ? 'Pick at least one to continue' : 'Pick one to continue'}
          </p>
        ) : comparingMulti ? (
          <p className={journeyStyles.exit}>
            Tick everything that fits, then let&rsquo;s compare.
          </p>
        ) : (
          <p className={journeyStyles.exit}>
            <Link to={`/${profile.slug}`}>Back to {profile.displayName}&rsquo;s page</Link>
          </p>
        )}

        {!canPersist ? (
          <p className={styles.privacyNote}>
            Your browser is blocking storage, so progress will not be saved if you leave.
          </p>
        ) : null}
      </div>

      {layer}
    </div>
  );
}
